import { DRIP_CAMPAIGN_ACTION_TYPES } from '../../constants';
import { RECIPIENT_BY_TRIGGER_TYPE } from '../DrawerView/Campaign/constants';

export const getCampaignTriggerOffsetModifier = (actions) => {
  const campaignTriggerAction = actions[0] || {};
  const { days_to_delay: daysToDelay } = campaignTriggerAction;

  if (!daysToDelay) {
    return 1;
  }

  return daysToDelay < 0 ? -1 : 1;
};

export const normalizeActions = (actions) =>
  actions.map((action) => {
    const { action_type: actionType } = action;

    const template = action.template || {};

    const actionTask =
      actionType === DRIP_CAMPAIGN_ACTION_TYPES.STATE_TRANSITION
        ? {
            nextProspectState: action.next_prospect_state,
          }
        : {
            templateName: template.template_name,
            templateId: template.template_id,
          };

    const daysToDelay = action.days_to_delay;
    const hasNoDelay = daysToDelay === 0;

    return {
      actionId: action.id,
      actionType,
      daysToDelay: hasNoDelay ? null : Math.abs(daysToDelay),
      hasNoDelay,
      ...actionTask,
    };
  });

export const normalizeProperties = (properties) =>
  properties.map((property) => ({
    propertyId: property.id,
    propertyName: property.name,
  }));

export const normalizeCampaign = (campaign) => {
  const {
    actions = [],
    properties = [],
    recipient_type,
    trigger_type,
  } = campaign;

  const recipient = recipient_type || RECIPIENT_BY_TRIGGER_TYPE[trigger_type];

  return {
    actions: normalizeActions(actions),
    associatedProperties: normalizeProperties(properties),
    campaignId: campaign.id,
    campaignName: campaign.title,
    campaignTrigger: trigger_type,
    campaignTriggerOffsetModifier: getCampaignTriggerOffsetModifier(actions),
    recipient,
    status: campaign.status,
  };
};

export const normalizeCampaigns = (campaigns) => {
  return campaigns.map((campaign) => {
    const triggerType = campaign.trigger_type;
    const recipient =
      campaign.recipient_type || RECIPIENT_BY_TRIGGER_TYPE[triggerType];

    return {
      associatedPropertiesCount: campaign.total_property_count,
      campaignId: campaign.campaign_id,
      campaignName: campaign.title,
      campaignTrigger: triggerType,
      recipient,
      status: campaign.status,
    };
  });
};

export const normalizePropertyList = (properties) =>
  properties.map((property) => ({
    isPropertyAvailableForTriggerType: property.availability,
    propertyId: property.property_id,
    propertyName: property.property_name,
  }));

export const normalizeTemplate = (template) => ({
  contentType: template.content_type,
  message: template.content,
  templateId: template.id,
  templateName: template.title,
  status: template.status.toLowerCase(),
  subject: template.subject,
});

export const sortColumnMap = {
  campaignName: 'title',
  templateName: 'title',
  subject: 'subject',
  status: 'status',
};

export const getQueryString = (params) => {
  const { page, pageSize, sortColumn, sortDirection, contentType } = params;
  const offSet = page * pageSize;
  const contentTypeQuery = contentType ? `&content_type=${contentType}` : '';
  return `?offset=${offSet}&page_size=${pageSize}&sort_column=${sortColumnMap[sortColumn]}&sort_direction=${sortDirection}${contentTypeQuery}`;
};

export const normalizeTemplates = (templates) => {
  return templates.map((template) => ({
    contentType: template.content_type,
    message: template.content || '',
    status: template.status.toLowerCase(),
    subject: template.subject || '',
    templateId: template.id,
    templateName: template.title || '',
  }));
};

export const transformActions = (actions, campaignTriggerOffsetModifier = 1) =>
  actions.map((action, index) => {
    const isCampaignTriggerAction = index === 0;
    const daysToDelay =
      isCampaignTriggerAction && action.daysToDelay
        ? action.daysToDelay * campaignTriggerOffsetModifier
        : action.daysToDelay;

    const actionEntries = [
      ['id', action.actionId],
      ['action_type', action.actionType],
      ['days_to_delay', daysToDelay],
      ['next_prospect_state', action.nextProspectState],
      ['template_id', action.templateId],
      ['is_deleted', action.isDeleted],
    ].filter(([_, value]) => value);

    if (action.hasNoDelay) {
      actionEntries.push(['days_to_delay', 0]);
    }

    return Object.fromEntries(actionEntries);
  });

export const transformCampaignSavePayload = (campaign) => {
  const {
    actions = [],
    associatedProperties = [],
    campaignTriggerOffsetModifier,
    recipient,
  } = campaign;
  return {
    actions: transformActions(actions, campaignTriggerOffsetModifier),
    property_ids: associatedProperties.map((property) => property.propertyId),
    ...(recipient && { recipient_type: recipient }),
    title: campaign.campaignName,
    trigger_type: campaign.campaignTrigger,
    status: campaign.status,
  };
};

export const transformEditedProperties = (properties) =>
  properties.map((property) => {
    const { isDeleted, propertyId } = property;

    const transformedProperty = {
      property_id: propertyId,
    };

    if (isDeleted) {
      transformedProperty.is_deleted = isDeleted;
    }
    return transformedProperty;
  });

export const transformCampaignUpdatePayload = (campaign) => {
  const {
    actions = [],
    associatedProperties = [],
    campaignName,
    campaignTrigger,
    campaignTriggerOffsetModifier,
    recipient,
    status,
  } = campaign;

  const campaignActions =
    actions.length && transformActions(actions, campaignTriggerOffsetModifier);
  const campaignProperties =
    associatedProperties.length &&
    transformEditedProperties(associatedProperties);

  const payloadEntries = [
    ['actions', campaignActions],
    ['properties', campaignProperties],
    ['recipient_type', recipient],
    ['status', status],
    ['title', campaignName],
    ['trigger_type', campaignTrigger],
  ].filter(([_, value]) => value);

  return Object.fromEntries(payloadEntries);
};

export const transformTemplateUpdatePayload = (template) => {
  const { message, subject, status, templateName } = template;

  const payloadEntries = [
    ['status', status],
    ['subject', subject],
    ['text', message],
    ['title', templateName],
  ].filter(([_, value]) => value);

  return Object.fromEntries(payloadEntries);
};

export const getPropertyListQueryString = ({ campaignId, triggerType }) => {
  const queryParams = [];

  if (campaignId) {
    queryParams.push(`campaign_id=${campaignId}`);
  }
  if (triggerType) {
    queryParams.push(`trigger_type=${triggerType}`);
  }

  return queryParams.length ? `?${queryParams.join('&')}` : '';
};
